import { OtsDateTime } from '../../components/OtsDateTime';
import { useState } from 'react';
import type { RrcLocationProfile } from '@ots-frontend/ots-data';
import { OtsMasterDetail } from '../../components/OtsMasterDetail';
import { OtsActionButton } from '../../components/OtsButton';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsDataTableEmpty } from '../../components/OtsDataTableCells';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { RrcLocationProfileDetails } from './RrcLocationProfileDetails';

export type RrcLocationProfileViewProps = {
  profile: RrcLocationProfile;
  versions?: RrcLocationProfile[];
  loadingVersions?: boolean;
  versionsError?: string;
  onRetry?: () => void;
  onEdit?: (profile: RrcLocationProfile) => void;
  onCopy?: (profile: RrcLocationProfile) => void;
  onDelete?: (profile: RrcLocationProfile) => void;
  canEdit?: (profile: RrcLocationProfile) => boolean;
  canDelete?: (profile: RrcLocationProfile) => boolean;
  readOnly?: boolean;
  compact?: boolean;
};
export const RrcLocationProfileView = ({
  profile,
  versions = [],
  loadingVersions,
  versionsError,
  onRetry,
  onEdit,
  onCopy,
  onDelete,
  canEdit = () => true,
  canDelete = () => true,
  readOnly,
  compact,
}: RrcLocationProfileViewProps) => {
  const [selectedId, select] = useState(profile.id);
  const history = [...versions].sort(
    (left, right) => right.version - left.version
  );
  const selected =
    history.find((version) => version.id === selectedId) ?? profile;
  const actions = (
    <div className="flex flex-wrap justify-end gap-2">
      {!readOnly && onCopy && (
        <OtsActionButton size="compact" onClick={() => onCopy(selected)}>
          Copy as new version
        </OtsActionButton>
      )}
      {!readOnly && onEdit && canEdit(selected) && (
        <OtsActionButton
          size="compact"
          variant="primary"
          onClick={() => onEdit(selected)}
        >
          Edit profile
        </OtsActionButton>
      )}
      {!readOnly && onDelete && canDelete(selected) && (
        <OtsActionButton
          size="compact"
          variant="destructive"
          onClick={() => onDelete(selected)}
        >
          Delete profile
        </OtsActionButton>
      )}
    </div>
  );
  return (
    <div className="@container space-y-4">
      {readOnly && (
        <OtsInlineAlert
          title="View only"
          size={compact ? 'compact' : 'default'}
        >
          Open Location Profiles in the owning tenant to manage this record.
        </OtsInlineAlert>
      )}
      <OtsMasterDetail
        navigationTitle="Version history"
        navigationLabel="Location profile versions"
        items={history.map((version) => ({
          id: version.id,
          label: `Version ${version.version} · ${version.status ?? 'Unknown'}`,
          ariaLabel: `Location profile version ${version.version}`,
          content: (
            <>
              <p className="mb-1 text-sm font-semibold text-[var(--ots-secondary)]">
                Version {version.version}
              </p>
              <ComplianceStatusBadge status={version.status} compact />
              <p className="mt-2 text-xs text-[var(--ots-text-muted)]">
                <OtsDateTime
                  value={version.effectiveStartDttm}
                  empty="Not set"
                />
              </p>
              <p className="mt-1 text-xs text-[var(--ots-text-muted)]">
                <OtsDateTime
                  value={version.effectiveEndDttm}
                  empty="No end date"
                />
              </p>
            </>
          ),
        }))}
        selectedId={selected.id}
        onSelect={select}
        compact={compact}
        navigationState={
          loadingVersions ? (
            <OtsDataTableEmpty
              loading
              loadingText="Loading location profile versions…"
            >
              {null}
            </OtsDataTableEmpty>
          ) : versionsError ? (
            <OtsInlineAlert
              tone="danger"
              title="History unavailable"
              action={
                onRetry && (
                  <OtsActionButton size="compact" onClick={onRetry}>
                    Try again
                  </OtsActionButton>
                )
              }
            >
              {versionsError}
            </OtsInlineAlert>
          ) : !history.length ? (
            <p className="text-sm text-[var(--ots-text-muted)]">
              No additional versions found.
            </p>
          ) : undefined
        }
      >
        <RrcLocationProfileDetails
          profile={selected}
          compact={compact}
          actions={actions}
        />
      </OtsMasterDetail>
    </div>
  );
};
